import {applyCommands,createDocument,evaluateDataset,makeColumn,serializeDocument,type ScientificDocument,type Command} from '@akyo/science-core/document';
import {curveObjects,curveObjectsCommand,proposeSeriesTrace} from '@akyo/science-ui/document-curves';
import type {GraphyoActivityConfig} from './graphyo-activity';

/** Empty measured columns; the preset supplies axes, never experimental results. */
export function emptyTitrationAnswer(config:GraphyoActivityConfig):string {
 let doc=createDocument('pH = f(V)',config.columns.map(c=>makeColumn(c.name,c.symbol,c.unit)));
 doc=applyCommands(doc,Array.from({length:config.initialRows??5},()=>({type:'add-row' as const,datasetId:doc.datasets[0].id})));
 const view=doc.views[0];
 doc=applyCommands(doc,[{type:'view',view:{...view,configuration:{
  x:{min:0,max:25,step:5},y:{min:0,max:14,step:2},
  imposedFields:['title','x.quantity','x.symbol','x.unit','y.quantity','y.symbol','y.unit'],
  modeling:false,showR2:false,showEquation:false,
 }}}]);
 return serializeDocument(doc);
}

export function titrationMeasurementError(doc:ScientificDocument,minimum=2):string|null {
 const data=doc.datasets[0],[x,y]=data.columns,cells=evaluateDataset(doc,data.id);
 let count=0,previous=-Infinity;
 for(const [i,row] of data.rows.filter(r=>!r.deleted).entries()){
  const values=cells.get(row.id)!,vx=values[x.id],vy=values[y.id];
  if(vx.status==='missing'&&vy.status==='missing')continue;
  if(vx.status!=='number'||vy.status!=='number')return `Ligne ${i+1} : saisissez un volume et un pH numériques.`;
  if(vx.value<0)return `Ligne ${i+1} : le volume ne peut pas être négatif.`;
  if(vx.value<=previous)return `Ligne ${i+1} : les volumes doivent être distincts et rangés dans l’ordre croissant.`;
  previous=vx.value;count++;
 }
 return count<minimum?`Saisissez au moins ${minimum===2?'deux':'trois'} couples volume / pH.`:null;
}

export function titrationIsPlotted(doc:ScientificDocument):boolean {
 return doc.views[0].configuration.competenceoTitrationPlotted===true||curveObjects(doc.views[0]).traces.length>0;
}

export function traceTitration(doc:ScientificDocument):ScientificDocument {
 const error=titrationMeasurementError(doc);if(error)throw Error(error);
 const [x,y]=doc.datasets[0].columns;
 const view={...doc.views[0],xColumnId:x.id,activeYColumnId:y.id,configuration:{...doc.views[0].configuration,competenceoTitrationPlotted:true}};
 if(!view.yColumnIds.includes(y.id))view.yColumnIds=[y.id,...view.yColumnIds];
 const objects=curveObjects(view);
 const previous=objects.traces.find(t=>t.yColumnId===y.id&&t.xColumnId===view.xColumnId);
 const trace=proposeSeriesTrace(doc,view,y.id,previous?.id);
 return applyCommands(doc,[curveObjectsCommand(view,{...objects,traces:[...objects.traces.filter(t=>t.id!==trace.id),trace]})]);
}

/** Raw inputs and the graph share one document. Never retain an obsolete line. */
export function editTitration(doc:ScientificDocument,commands:Command[]):ScientificDocument {
 const plotted=titrationIsPlotted(doc);
 let next=applyCommands(doc,commands);
 if(!plotted)return next;
 if(!titrationMeasurementError(next))return traceTitration(next);
 const view=next.views[0],objects=curveObjects(view),[x,y]=next.datasets[0].columns;
 next=applyCommands(next,[curveObjectsCommand({...view,configuration:{...view.configuration,competenceoTitrationPlotted:true}},
  {...objects,traces:objects.traces.filter(t=>t.xColumnId!==x.id||t.yColumnId!==y.id)})]);
 return next;
}

export function deriveTitration(input:ScientificDocument):ScientificDocument {
 const error=titrationMeasurementError(input,3);if(error)throw Error(error);
 let doc=traceTitration(input);
 const data=doc.datasets[0],[x,y]=data.columns;
 let derivative=data.columns.find(c=>c.transform?.version===2&&c.transform.kind==='derivative'&&c.transform.xColumnId===x.id&&c.transform.yColumnId===y.id);
 if(!derivative){
  const symbols=new Set(data.columns.map(c=>c.symbol));let symbol='dpH';
  for(let n=2;symbols.has(symbol);n++)symbol='dpH'+n;
  derivative={...makeColumn('Dérivée du pH',symbol,'1/mL'),kind:'calculated',transform:{version:2,kind:'derivative',xColumnId:x.id,yColumnId:y.id}};
  doc=applyCommands(doc,[{type:'add-column',datasetId:data.id,column:derivative}]);
 }
 const view=doc.views[0];
 return applyCommands(doc,[{type:'view',view:{...view,xColumnId:x.id,activeYColumnId:y.id,
  yColumnIds:[y.id,derivative.id],configuration:{...view.configuration,rightAxisColumnId:derivative.id},
 }}]);
}
